import get from 'lodash/get'
import map from 'lodash/map'
import uniq from 'lodash/uniq'
import compact from 'lodash/compact'
import chunk from 'lodash/chunk'

import {createSelector} from 'reselect'

import * as pane from './pane'

import {SCORE_TYPE_ALL, EVENT_TYPE_ALL, PAGINATION_SET, REVIEWS_PER_PAGE} from '../const'

// Functions
// lodash/compact removes null, undefined, and empty strings
const eventTypes = (reviews) => compact(uniq(map(reviews, 'event')))

// Standard
export const featuredReviews = (state) => get(state, 'reviews.featuredReviews')
export const paginationCurrentPageIndex = (state) => get(state, 'reviews.currentPageIndex')
export const paginationPageCount = (state) => get(state, 'reviews.pageCount')
export const paginationArray = (state) =>
  chunk(get(state, 'reviews.reviewsPagination'), REVIEWS_PER_PAGE)
export const filteredArray = (state) => get(state, 'reviews.reviewsPagination')
export const currentReviewImage = (state) => get(state, 'reviews.currentReviewImage')
export const featuredReviewsIsLoaded = (state) => get(state, 'reviews.featuredReviewsIsLoaded')
export const productReviews = (state) => get(state, 'reviews.productReviews')
export const productReviewsArray = (state) => get(productReviews(state), 'reviews')
export const starDistribution = (state) => get(productReviews(state), 'starDistribution')
export const fetchError = (state) => get(state, 'reviews.fetchError')
export const totalReviews = (state) => get(state, 'reviews.totalReviews')
export const averageScore = (state) => get(state, 'reviews.averageScore')
export const reviewImageModalIsOpen = (state) => get(state, 'reviews.reviewImageModalIsOpen')
export const currentEventFilter = (state) => get(state, 'reviews.currentEventFilter')
export const currentScoreFilter = (state) => get(state, 'reviews.currentScoreFilter')
export const eventFilterDropdownIsOpen = (state) => get(state, 'reviews.eventFilterDropdownIsOpen')
export const scoreFilterDropdownIsOpen = (state) => get(state, 'reviews.scoreFilterDropdownIsOpen')
export const elementIsScrollready = (state) => get(state, 'reviews.scrollReady')
export const reviewsFilterModalIsOpen = (state) => get(state, 'reviews.reviewsFilterModalIsOpen')

// Computed
export const scoreFilterIsAll = createSelector(
  currentScoreFilter,
  (score) => score === SCORE_TYPE_ALL,
)

export const noFiltersApplied = createSelector(
  currentEventFilter,
  currentScoreFilter,
  (event, score) => event === EVENT_TYPE_ALL && score === SCORE_TYPE_ALL,
)

export const eventFilterIsApplied = createSelector(
  currentEventFilter,
  (event) => event !== EVENT_TYPE_ALL,
)

export const scoreFilterIsApplied = createSelector(
  currentScoreFilter,
  (score) => score !== SCORE_TYPE_ALL,
)

export const filterIsApplied = createSelector(
  eventFilterIsApplied,
  scoreFilterIsApplied,
  (eventFilter, scoreFilter) => (eventFilter && !scoreFilter) || (!eventFilter && scoreFilter),
)

export const allFiltersApplied = createSelector(
  eventFilterIsApplied,
  scoreFilterIsApplied,
  (eventFilter, scoreFilter) => eventFilter && scoreFilter,
)

export const paginationNumberDisplayed = createSelector(pane.windowIsDesktop, (isDesktop) => {
  const numberDisplayed = isDesktop ? PAGINATION_SET.DESKTOP : PAGINATION_SET.MOBILE
  return numberDisplayed
})

export const currentPage = createSelector(
  paginationCurrentPageIndex,
  (currentIndex) => currentIndex + 1,
)

export const isNormalPagination = createSelector(
  paginationPageCount,
  paginationNumberDisplayed,
  (pageCount, numberDisplayed) => pageCount <= numberDisplayed && pageCount > 0,
)

export const isFirstSetPagination = createSelector(
  paginationPageCount,
  currentPage,
  paginationNumberDisplayed,
  (pageCount, current, numberDisplayed) => pageCount > numberDisplayed && current < numberDisplayed,
)

export const isMiddlePagination = createSelector(
  paginationPageCount,
  currentPage,
  paginationNumberDisplayed,
  (pageCount, current, numberDisplayed) =>
    current > numberDisplayed - 1 &&
    pageCount > numberDisplayed &&
    current <= pageCount - numberDisplayed + 1,
)

export const isLastSetPagination = createSelector(
  paginationPageCount,
  currentPage,
  paginationNumberDisplayed,
  (pageCount, current, numberDisplayed) =>
    pageCount > numberDisplayed && current > pageCount - numberDisplayed + 1,
)

export const reviewsNotOk = createSelector(productReviews, (reviews) => reviews === false)

export const eventFilterIsAll = createSelector(
  currentEventFilter,
  (currentFilter) => currentFilter === EVENT_TYPE_ALL,
)

export const reviewsEventTypes = createSelector(productReviewsArray, eventTypes)

export const paginationRequired = createSelector(paginationPageCount, (pageCount) => pageCount > 1)

export const displayReviews = createSelector(
  paginationCurrentPageIndex,
  paginationArray,
  (currentPageIndex, array) => array[currentPageIndex],
)

export const hasReviews = createSelector(totalReviews, (total) => total > 0)

export const averageFit = (state) =>
  Math.round(get(productReviews(state), 'ratingsAverages.fit', 0))
export const averageQuality = (state) =>
  Math.round(get(productReviews(state), 'ratingsAverages.quality', 0))
export const averageService = (state) =>
  Math.round(get(productReviews(state), 'ratingsAverages.service', 0))
